import { createRouter, createWebHistory } from 'vue-router'
import { useSettingsStore } from '@/stores/settings'

const LICENSE_LEVEL = {
  free: 0,
  trial: 1,
  premium: 2,
  club: 3,
  club_coach: 4
}

function getUserLicense() {
  const stored = localStorage.getItem('dashboard_license')
  const settingsStore = useSettingsStore()
  if (settingsStore.profile?.licenseType) {
    return settingsStore.profile.licenseType
  }
  return stored || 'free'
}

function meetsLicense(userLicense, minLicense) {
  const userLevel = LICENSE_LEVEL[userLicense] ?? 0
  const minLevel = LICENSE_LEVEL[minLicense] ?? 0
  return userLevel >= minLevel
}

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: 'Login', public: true }
  },
  {
    path: '/sso',
    name: 'sso',
    component: () => import('@/views/SsoView.vue'),
    meta: { title: 'SSO', public: true }
  },
  {
    path: '/',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/HomeView.vue'),
        meta: { title: 'Dashboard' }
      },
      {
        path: 'games',
        name: 'games',
        component: () => import('@/views/GamesView.vue'),
        meta: { title: 'Partidos' }
      },
      {
        path: 'games/:uuid',
        name: 'fileMatch',
        component: () => import('@/views/FileMatch.vue'),
        meta: { title: 'Ficha del partido' }
      },
      {
        path: 'games/:uuid/evaluation',
        name: 'matchEvaluation',
        component: () => import('@/views/MatchEvaluationView.vue'),
        meta: { title: 'Evaluación del partido', minLicense: 'trial' }
      },
      {
        path: 'games/:uuid/self-eval',
        name: 'playerSelfEval',
        component: () => import('@/views/PlayerSelfEvalView.vue'),
        meta: { title: 'Valoración de jugadoras', minLicense: 'trial' }
      },
      {
        path: 'teams',
        name: 'teams',
        component: () => import('@/views/TeamsView.vue'),
        meta: { title: 'Equipos' }
      },
      {
        path: 'teams/new',
        name: 'team-new',
        component: () => import('@/views/TeamFormView.vue'),
        meta: { title: 'Nuevo equipo' }
      },
      {
        path: 'teams/:uuid/edit',
        name: 'team-edit',
        component: () => import('@/views/TeamFormView.vue'),
        meta: { title: 'Editar equipo' }
      },
      {
        path: 'players',
        name: 'players',
        component: () => import('@/views/PlayersView.vue'),
        meta: { title: 'Jugadores' }
      },
      {
        path: 'coaches',
        name: 'coaches',
        component: () => import('@/views/CoachesView.vue'),
        meta: { title: 'Entrenadores', requiredLicense: 'club' }
      },
      {
        path: 'coaches/new',
        name: 'coach-new',
        component: () => import('@/views/CoachFormView.vue'),
        meta: { title: 'Nuevo entrenador', requiredLicense: 'club' }
      },
      {
        path: 'coaches/:id/edit',
        name: 'coach-edit',
        component: () => import('@/views/CoachFormView.vue'),
        meta: { title: 'Editar entrenador', requiredLicense: 'club' }
      },
      {
        path: 'coaches/:id',
        name: 'coach-detail',
        component: () => import('@/views/CoachDetailView.vue'),
        meta: { title: 'Ficha del entrenador', requiredLicense: 'club' }
      },
      {
        path: 'analytics',
        component: () => import('@/views/AnalyticsLayout.vue'),
        meta: { title: 'Analíticas', minLicense: 'premium' },
        redirect: { name: 'analytics-advanced' },
        children: [
          {
            path: '',
            name: 'analytics-advanced',
            component: () => import('@/views/AdvancedAnalytics.vue'),
            meta: { title: 'Análisis Avanzado' }
          },
          {
            path: 'accumulated',
            name: 'analytics-accumulated',
            component: () => import('@/views/AccumulatedStats.vue'),
            meta: { title: 'Estadísticas Acumuladas' }
          },
          {
            path: 'evolution',
            name: 'analytics-evolution',
            component: () => import('@/views/EvolutionAnalytics.vue'),
            meta: { title: 'Evolución' }
          }
        ]
      },
      {
        path: 'players/:uuid/stats',
        name: 'player-stats',
        component: () => import('@/views/PlayerStatsView.vue'),
        meta: { title: 'Estadísticas', minLicense: 'premium' }
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { title: 'Ajustes' }
      },
      {
        path: 'upgrade-required',
        name: 'upgrade-required',
        component: () => import('@/views/UpgradeRequired.vue'),
        meta: { title: 'Actualizar plan' }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('dashboard_token')

  if (to.meta.requiresAuth && !token) {
    next({ name: 'login' })
    return
  }

  if (to.name === 'login' && token) {
    next()
    return
  }

  const minLicense = to.matched.reduce((acc, record) => record.meta.minLicense || acc, null)
  if (minLicense && !meetsLicense(getUserLicense(), minLicense)) {
    next({ name: 'upgrade-required', query: { from: to.name } })
    return
  }

  const requiredLicense = to.matched.reduce((acc, record) => record.meta.requiredLicense || acc, null)
  if (requiredLicense && getUserLicense() !== requiredLicense) {
    next({ name: 'upgrade-required', query: { from: to.name } })
    return
  }

  next()
})

export default router
